import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  Clock,
  Printer,
  RefreshCw,
  Truck,
} from "react-feather";
import { Button, Card, Empty, Select, Space, Tag } from "antd";
import PackagesViewer from "../PackagesViewer";
import copyToClipboard from "../../../utils/copyToClipboard";
import { formatDateTimeWithoutSeconds } from "../../../utils/dateFormatter";

const { Option } = Select;

const Step4CarrierDispatch = ({
  group,
  providerSelections = {},
  onProviderSelect,
  onPlaceOrder,
  onPrint,
  printLoading = {},
  onRecalculateRate,
  rateLoading = {},
  onFetchTracking,
  trackingLoading = {},
  trackingData = {},
  onBack,
  onFulfillMore,
  hasMoreUnassigned = false,
  t,
}) => {
  if (!group) {
    return (
      <div style={{ padding: "32px 16px", textAlign: "center" }}>
        <Empty
          description={t(
            "stepper.noShipmentDesc",
            "Please bundle loose packages in Step 3 to create a shipping order first.",
          )}
        />
        <div style={{ marginTop: "16px" }}>
          <Button onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <ArrowLeft size={16} />
            <span>{t("stepper.backToBundle", "Back to Bundle Packages")}</span>
          </Button>
        </div>
      </div>
    );
  }

  const shippingOrder = group.shipping_order || {};
  const attention = shippingOrder?.attention || group?.attention;
  const hasAttention = !!attention?.needs_attention;
  const isOrderPlaced =
    !!shippingOrder.tracking_number && shippingOrder.tracking_number !== "";
  const providerValue =
    providerSelections[shippingOrder.id] ||
    (shippingOrder.provider && shippingOrder.provider !== ""
      ? shippingOrder.provider
      : "aramex");

  const tracking = trackingData[shippingOrder.id];
  const isRateLoading = !!rateLoading[shippingOrder.id];
  const isPrintLoading = !!printLoading[shippingOrder.id];
  const isTrackingLoading = !!trackingLoading[shippingOrder.id];

  return (
    <div>
      {/* Top navigation & info */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          padding: "12px 16px",
          backgroundColor: "#f8fafc",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Button
            onClick={onBack}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <ArrowLeft size={16} />
            <span>{t("stepper.backToBundle", "Back to Bundle Packages")}</span>
          </Button>

          <span style={{ fontWeight: 600, fontSize: "14px", color: "#1e293b" }}>
            {t("orderDetails.shippingOrderHeader", "Shipping Order")} #{shippingOrder.id}
          </span>
          {hasAttention && (
            <Tag
              color="error"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontWeight: 600,
              }}
            >
              <AlertTriangle size={12} />
              {t("shipping.needsAttention", "Needs Attention")}
            </Tag>
          )}
          {(() => {
            const lower = shippingOrder.status?.toLowerCase();
            if (lower === "returned") {
              return (
                <Tag color="warning">
                  <AlertTriangle
                    size={12}
                    style={{ marginRight: 4, verticalAlign: "middle" }}
                  />
                  {t("order.status.returned", "Returned")}
                </Tag>
              );
            }
            if (lower === "lostconfiscated" || lower === "lost_confiscated") {
              return (
                <Tag color="error">
                  <AlertTriangle
                    size={12}
                    style={{ marginRight: 4, verticalAlign: "middle" }}
                  />
                  {t("order.status.lostconfiscated", "Lost / Confiscated")}
                </Tag>
              );
            }
            if (isOrderPlaced) {
              return (
                <Tag color="success">
                  <CheckCircle
                    size={12}
                    style={{ marginRight: 4, verticalAlign: "middle" }}
                  />
                  {t("orderDetails.dispatched", "Dispatched")}
                </Tag>
              );
            }
            return (
              <Tag color="processing">
                <Clock
                  size={12}
                  style={{ marginRight: 4, verticalAlign: "middle" }}
                />
                {t("orderDetails.readyToDispatch", "Ready to Dispatch")}
              </Tag>
            );
          })()}
        </div>

        {hasMoreUnassigned && onFulfillMore && (
          <Button
            type="default"
            onClick={onFulfillMore}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <span>{t("stepper.fulfillMore", "+ Fulfill More Items")}</span>
          </Button>
        )}
      </div>

      {/* Main Dispatch Card */}
      <Card
        style={{
          borderRadius: "12px",
          border: hasAttention ? "1px solid #ffccc7" : "1px solid #e2e8f0",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          marginBottom: "20px",
        }}
      >
        {hasAttention && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              padding: "10px 14px",
              backgroundColor: "#fff2f0",
              border: "1px solid #ffccc7",
              borderRadius: "8px",
              marginBottom: "20px",
              color: "#cf1322",
            }}
          >
            <AlertTriangle
              size={18}
              style={{ flexShrink: 0, marginTop: "2px", color: "#cf1322" }}
            />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <span>{t("shipping.needsAttention", "Needs Attention")}</span>
                {attention.at && (
                  <span
                    style={{
                      fontWeight: "normal",
                      fontSize: "12px",
                      color: "#8c8c8c",
                    }}
                  >
                    ({formatDateTimeWithoutSeconds(attention.at)})
                  </span>
                )}
              </div>
              {attention.note && (
                <div
                  style={{
                    fontSize: "13px",
                    color: "#434343",
                    marginTop: "4px",
                    lineHeight: "1.4",
                  }}
                >
                  {attention.note}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Header Summary */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "20px",
            paddingBottom: "16px",
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <div>
            <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", fontWeight: 700 }}>
              {t("stepper.carrierDispatchTitle", "Carrier Dispatch & Waybill")}
            </h3>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", fontSize: "13px" }}>
              <span>
                <strong style={{ color: "#64748b" }}>
                  {t("orderDetails.status", "Status")}:
                </strong>{" "}
                {(() => {
                  const statusStr = shippingOrder.status;
                  if (!statusStr) {
                    return (
                      <Tag color={isOrderPlaced ? "green" : "blue"}>
                        {t("order.status.ready", "Ready")}
                      </Tag>
                    );
                  }
                  const lower = statusStr.toLowerCase();
                  let tagColor = "default";
                  if (lower === "arrived") tagColor = "green";
                  else if (lower === "returned") tagColor = "warning";
                  else if (
                    lower === "lostconfiscated" ||
                    lower === "lost_confiscated"
                  )
                    tagColor = "error";
                  else if (
                    lower === "tracking" ||
                    lower === "shipping"
                  )
                    tagColor = "processing";
                  else if (isOrderPlaced) tagColor = "green";
                  return (
                    <Tag color={tagColor}>
                      {t(`order.status.${lower}`, statusStr)}
                    </Tag>
                  );
                })()}
              </span>
              <span>
                <strong style={{ color: "#64748b" }}>
                  {t("orderDetails.createdAt", "Created At")}:
                </strong>{" "}
                {shippingOrder.created_at
                  ? formatDateTimeWithoutSeconds(shippingOrder.created_at)
                  : t("table.common.na", "N/A")}
              </span>
              {shippingOrder.tracking_number && (
                <span>
                  <strong style={{ color: "#64748b" }}>
                    {t("orderDetails.trackingNumber", "Tracking #")}:
                  </strong>{" "}
                  <span
                    style={{
                      cursor: "pointer",
                      fontWeight: 600,
                      color: "#0284c7",
                      textDecoration: "underline",
                    }}
                    onDoubleClick={() =>
                      copyToClipboard(shippingOrder.tracking_number, {
                        successMessage: t("copy.trackingNumberCopied", "Tracking number copied"),
                      })
                    }
                    title={t("copy.doubleClickToCopy", "Double-click to copy")}
                  >
                    {shippingOrder.tracking_number}
                  </span>
                </span>
              )}
            </div>
          </div>

          {/* Rate & Estimated Cost */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              padding: "12px 18px",
              backgroundColor: "#f0f9ff",
              borderRadius: "10px",
              border: "1px solid #bae6fd",
              boxShadow: "0 2px 6px rgba(2, 132, 199, 0.08)",
            }}
          >
            <div>
              <span style={{ fontSize: "11px", color: "#0369a1", display: "block", fontWeight: 600, textTransform: "uppercase" }}>
                {t("packing.estCost", "Estimated Shipping Cost:")}
              </span>
              <span style={{ fontSize: "19px", fontWeight: 800, color: "#0284c7" }}>
                {group.estimated_shipping_cost?.amount !== null &&
                group.estimated_shipping_cost?.amount !== undefined
                  ? `${Number(group.estimated_shipping_cost.amount).toFixed(2)} ${
                      group.estimated_shipping_cost.currency || "AED"
                    }`
                  : t("table.common.na", "N/A")}
              </span>
            </div>

            {onRecalculateRate && (
              <Button
                size="small"
                icon={<RefreshCw size={13} style={{ marginRight: 4 }} />}
                loading={isRateLoading}
                onClick={() => onRecalculateRate(shippingOrder.id)}
                style={{ borderRadius: "6px", fontWeight: 500 }}
                title="Query live rate from Aramex"
              >
                {t("orderDetails.calculateRate", "Calculate Rate")}
              </Button>
            )}
          </div>
        </div>

        {/* Action Row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
            padding: "16px 20px",
            backgroundColor: "#f8fafc",
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
            marginBottom: "24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontWeight: 600, fontSize: "13px" }}>
              {t("orderDetails.carrierProvider", "Carrier Provider:")}
            </span>
            <Select
              style={{ width: 160 }}
              value={providerValue}
              disabled={isOrderPlaced}
              onChange={(val) => onProviderSelect?.(shippingOrder.id, val)}
            >
              <Option value="aramex">Aramex</Option>
              <Option value="vnlin">Vnlin</Option>
              <Option value="imile">Imile</Option>
            </Select>
          </div>

          <Space size="middle">
            {!isOrderPlaced ? (
              <Button
                type="primary"
                size="large"
                style={{
                  background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                  boxShadow: "0 4px 14px rgba(5, 150, 105, 0.35)",
                  border: "none",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: 600,
                }}
                onClick={() => onPlaceOrder?.(shippingOrder.id, providerValue)}
              >
                <Truck size={18} />
                <span>{t("placeOrder.title", "Place Order with Carrier")}</span>
              </Button>
            ) : (
              <>
                <Button
                  type="primary"
                  size="large"
                  icon={<Printer size={18} style={{ marginRight: 6 }} />}
                  loading={isPrintLoading}
                  disabled={!shippingOrder.tracking_number}
                  onClick={() => onPrint?.(shippingOrder.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 600,
                    borderRadius: "8px",
                    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                    boxShadow: "0 4px 14px rgba(37, 99, 235, 0.35)",
                    border: "none",
                  }}
                >
                  {t("placeOrder.print", "Print Waybill Label")}
                </Button>

                {onFetchTracking && (
                  <Button
                    size="large"
                    loading={isTrackingLoading}
                    onClick={() => onFetchTracking(shippingOrder.id)}
                  >
                    {t("orderDetails.fetchAramexTracking", "Track Shipment")}
                  </Button>
                )}
              </>
            )}
          </Space>
        </div>

        {/* Packages in this shipment */}
        <div>
          <h4 style={{ margin: "0 0 12px 0", fontSize: "15px", fontWeight: 600 }}>
            {t("packing.packagesInShipment", "Physical Boxes in this Shipment")}
          </h4>
          <PackagesViewer
            shippingOrderId={shippingOrder.id}
            refreshKey={group.packing_summary?.packages_count}
          />
        </div>

        {/* Aramex Tracking Events if fetched */}
        {!!tracking?.events?.length && (
          <div style={{ marginTop: "24px" }}>
            <h4 style={{ marginBottom: "8px", fontSize: "15px", fontWeight: 600 }}>
              {t("orderDetails.aramexEvents", "Tracking Updates")}
            </h4>
            <div style={{ overflowX: "auto" }}>
              <table className="table table-striped" style={{ fontSize: "13px" }}>
                <thead>
                  <tr>
                    <th>{t("orderDetails.eventCode", "Code")}</th>
                    <th>{t("orderDetails.eventDescription", "Description")}</th>
                    <th>{t("orderDetails.location", "Location")}</th>
                    <th>{t("orderDetails.eventDate", "Time")}</th>
                  </tr>
                </thead>
                <tbody>
                  {tracking.events.map((event, idx) => (
                    <tr key={idx}>
                      <td>{event.code || "N/A"}</td>
                      <td>{event.description || "N/A"}</td>
                      <td>{event.location || "N/A"}</td>
                      <td>{event.datetime || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Step4CarrierDispatch;
